// Códigos de error definidos por abakoo-api (RFC 7807 ProblemDetails)
export type ApiErrorCode =
  | 'PDF_PENDING'
  | 'PDF_FAILED'
  | 'VALIDATION_ERROR'
  | 'WRONG_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'ACCOUNT_DISABLED'
  | 'EMAIL_NOT_VERIFIED'
  | 'PHONE_NOT_VERIFIED'
  | 'AUTHENTICATION_FAILED'
  | 'REGISTRATION_FAILED'
  | 'OTP_WRONG'
  | 'OTP_EXPIRED'
  | 'OTP_MAX_ATTEMPTS'
  | 'OTP_NOT_FOUND'
  | 'INVALID_RESET_TOKEN'
  | 'REFRESH_FAILED'
  | 'FEEDBACK_RATE_LIMIT'
  // Paginación por cursor (clients, products, invoices)
  | 'INVALID_CURSOR'
  | 'CURSOR_EXPIRED'
  | 'CURSOR_FILTERS_CHANGED'
  // Account module
  | 'INVALID_CURRENT_PASSWORD'
  | 'PASSWORD_REUSE_NOT_ALLOWED'
  | 'PHONE_ALREADY_IN_USE'
  | 'PHONE_UNCHANGED'
  | 'PHONE_CHANGE_RATE_LIMIT'
  // Fiscal Profile module
  | 'INVALID_RFC_FORMAT'
  | 'INVALID_REGIMEN'
  | 'REGIMEN_DUPLICATED'
  | 'REGIMEN_NOT_FOUND'
  | 'REGIMEN_LAST_ACTIVE'
  | 'INVALID_LOCATION'
  | 'INVALID_TIMEZONE'
  | 'FISCAL_PROFILE_NOT_FOUND'
  // Certificates module
  | 'INVALID_ACCOUNT_PASSWORD'
  | 'INVALID_CER_FILE'
  | 'INVALID_KEY_FILE'
  | 'INVALID_KEY_PASSWORD'
  | 'CERT_KEY_MISMATCH'
  | 'CERT_EXPIRED'
  | 'CERT_NOT_YET_VALID'
  | 'CERT_TYPE_MISMATCH'
  | 'RFC_MISMATCH'
  | 'FISCAL_PROFILE_REQUIRED'
  | 'CERTIFICATE_DUPLICATED'
  | 'CERTIFICATE_NOT_FOUND'
  | 'SW_UPLOAD_FAILED'
  | 'CIEC_NOT_FOUND'
  | 'DOMAIN_ERROR'
  // Clients module
  | 'CLIENT_NOT_FOUND'
  | 'CLIENT_RFC_DUPLICATED'
  | 'CLIENT_IS_SYSTEM'
  | 'INVALID_RAZON_SOCIAL'
  | 'INVALID_CFDI_USE'
  | 'INVALID_PAYMENT_FORM'
  // Products module
  | 'PRODUCT_NOT_FOUND'
  | 'PRODUCT_SKU_DUPLICATED'
  | 'INVALID_SAT_PRODUCT_KEY'
  | 'INVALID_SAT_UNIT_KEY'
  | 'INVALID_OBJETO_IMP'
  | 'INVALID_CURRENCY'
  | 'INVALID_UNIT_PRICE'
  | 'INVALID_IMPUESTO_SHAPE'
  | 'INVALID_IMPUESTO_CODE'
  | 'INVALID_IMPUESTO_FACTOR'
  | 'INVALID_IMPUESTO_TASA'
  | 'INVALID_IMPUESTO_OBJETO_MISMATCH'
  // Invoices module
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'DRAFT_NOT_EDITABLE'
  | 'DRAFT_INCOMPLETE'
  | 'DRAFT_VERSION_CONFLICT'
  | 'DRAFT_OP_INDEX_OUT_OF_RANGE'
  | 'SUBSCRIPTION_REQUIRED'
  | 'INVOICE_LIMIT_EXCEEDED'
  | 'CSD_REQUIRED'
  // Requisitos de emisión validados por InvoiceEligibilityRules (abakoo-api) tanto al
  // CREAR el borrador como al timbrarlo.
  | 'CSD_EXPIRED'
  | 'REGIMEN_REQUIRED'
  | 'INVOICE_NOT_FOUND'
  | 'TIMBRA_EMPTY_RESPONSE'
  | 'TIMBRA_UNAVAILABLE'
  | 'SW_REJECTED'
  | 'INVOICE_NOT_PPD'
  | 'INVOICE_NOT_VIGENTE'
  | 'INVOICE_FULLY_PAID'
  | 'PAYMENT_EXCEEDS_BALANCE'
  | 'EXCEEDS_BALANCE'
  | 'CURRENCY_NOT_SUPPORTED'
  | 'SAT_RULE_VIOLATION'
  | 'EQUIVALENCIA_DR_MISSING'
  | 'STAMP_OUTCOME_UNKNOWN'
  | 'EXPORTACION_NOT_SUPPORTED'
  | 'FILE_NOT_AVAILABLE'
  | 'ACUSE_NOT_AVAILABLE'
  // Subscription module
  | 'PLAN_VERSION_NOT_FOUND'
  | 'STRIPE_PRICE_NOT_SET'
  | 'CHECKOUT_FAILED'
  | 'ACTIVE_SUBSCRIPTION_EXISTS'
  | 'SUBSCRIPTION_NOT_FOUND'
  | 'CHANGE_PLAN_FAILED'
  | 'SAME_PLAN_ERROR'
  | 'ALREADY_CHANGED_THIS_PERIOD'
  | 'HAS_PENDING_CHANGE'
  | 'PAYMENT_PROVIDER_ERROR'
  | 'SUBSCRIPTION_NOT_ACTIVE'
  | 'PLAN_CHANGE_AUTO_CANCELED'
  | 'REQUIRES_CHECKOUT'
  | 'ONBOARDING_PREREQS_INCOMPLETE'
  | 'PROMO_CODE_INVALID'
  | 'UNKNOWN'

// Shape estándar de error de abakoo-api (RFC 7807)
export interface ProblemDetails {
  title: string
  status: number
  detail: string
  instance: string
  correlationId: string
  errorCode: ApiErrorCode
}

// Error tipado para todas las llamadas a la API.
// `data` almacena el body completo del error — útil para 403 con verificationToken.
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ApiErrorCode,
    message: string,
    public readonly correlationId?: string,
    public readonly data?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}
